import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import {
  GET_PLATFORM_SITE_MARKERS,
  UPDATE_PLATFORM_SITE_MARKERS,
} from '../../services/platformSettingsService/queries';
import {
  normalizePlatformSiteMarkers,
  refreshPlatformSiteMarkers,
} from '../../utils/platformSiteMarkers';
import { useWindowWidth } from '../../hooks/useMobileScreenListenerHook';
import PlatformSiteMarkersEditOverlay from './PlatformSiteMarkersEditOverlay';
import { PlatformSiteMarkersFormValues } from './PlatformSiteMarkersForm';
import {
  buildUpdatePlatformSiteMarkersInput,
  formatPlatformSiteMarkersLabel,
  toPlatformSiteMarkersFormValues,
} from './platformSiteMarkersAdminUtils';
import {
  PLATFORM_SITES_CARD_LABEL,
  PLATFORM_SITES_EDIT_BUTTON,
  PLATFORM_SITES_PAGE_SUBTITLE,
  PLATFORM_SITES_PAGE_TITLE,
} from './platformSiteMarkersCopy';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
} from '../../components/ui';

const UrlChipList = styled.ul`
  ${tw`flex flex-col gap-2 list-none m-0 p-0`}
`;

const UrlChip = styled.li`
  ${tw`px-3 py-2 text-sm break-all font-mono`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
`;

const EditBar = styled.div`
  ${tw`
    fixed inset-x-0 bottom-0 z-30 p-4
    md:static md:mt-6 md:p-0
  `}
  @media (max-width: 767px) {
    background-color: var(--color-bg-elevated);
    border-top: 1px solid var(--color-border);
  }
`;

export default function AdminPlatformSitesPage() {
  const { isMobile } = useWindowWidth();
  const [editOpen, setEditOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_PLATFORM_SITE_MARKERS, {
    fetchPolicy: 'network-only',
    onError: err => toast.error(`Failed to load platform URL markers: ${err.message}`),
  });

  const [updatePlatformSiteMarkers, { loading: saving }] = useMutation(
    UPDATE_PLATFORM_SITE_MARKERS,
    {
      onCompleted: async () => {
        toast.success('Platform URL markers saved');
        setEditOpen(false);
        await refetch();
        await refreshPlatformSiteMarkers();
      },
      onError: err => toast.error(`Failed to save platform URL markers: ${err.message}`),
    },
  );

  const markers = useMemo(
    () => normalizePlatformSiteMarkers(data?.platformSiteMarkers ?? []),
    [data?.platformSiteMarkers],
  );
  const formInitialValues = useMemo(() => toPlatformSiteMarkersFormValues(markers), [markers]);

  const handleSave = async (values: PlatformSiteMarkersFormValues) => {
    const input = buildUpdatePlatformSiteMarkersInput(values);
    if ('error' in input) {
      toast.error(input.error);
      return;
    }

    await updatePlatformSiteMarkers({
      variables: { input },
    });
  };

  return (
    <PageContainer width="md" canvas>
      <PageHeader
        eyebrow="Platform owner"
        title={PLATFORM_SITES_PAGE_TITLE}
        description={PLATFORM_SITES_PAGE_SUBTITLE}
      />

      {loading && <LoadingBlock label="Loading platform sites" />}
      {error && (
        <Alert tone="error" title="Could not load platform sites">
          {error.message}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Card>
            <CardHeader
              title={PLATFORM_SITES_CARD_LABEL}
              description={`${markers.length} site${markers.length === 1 ? '' : 's'}`}
            />
            {markers.length > 0 ? (
              <CardBody>
                <UrlChipList>
                  {markers.map(marker => (
                    <UrlChip key={marker}>{marker}</UrlChip>
                  ))}
                </UrlChipList>
              </CardBody>
            ) : (
              <EmptyState
                compact
                icon={faGlobe}
                title="No platform sites yet"
                description={formatPlatformSiteMarkersLabel(markers)}
              />
            )}
          </Card>

          <EditBar>
            <Button
              text={PLATFORM_SITES_EDIT_BUTTON}
              type="button"
              onClick={() => setEditOpen(true)}
              fullWidth={isMobile}
            />
          </EditBar>

          <PlatformSiteMarkersEditOverlay
            isOpen={editOpen}
            isMobile={isMobile}
            saving={saving}
            initialValues={formInitialValues}
            onClose={() => setEditOpen(false)}
            onSubmit={handleSave}
          />
        </>
      )}
    </PageContainer>
  );
}
